import type React from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useLocation } from "react-router"

import { useAuthentication } from "@/contexts"
import { useFlags } from "@/services/feature"
import { useUrl } from "@/utils/url"

import Divider from "@mui/material/Divider"
import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import type { ListItemButtonProps } from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import { styled } from "@mui/material/styles"

// Must cast to ListItemButton to avoid type error
// See https://mui.com/material-ui/guides/typescript/#complications-with-the-component-prop
const StyledListItemButton = styled((props: ListItemButtonProps) => (
  <ListItemButton {...props} />
))(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
})) as unknown as typeof ListItemButton

export const AdminSecondaryNav: React.FC = () => {
  const { t } = useTranslation("common")
  const { pathname } = useLocation()
  const { admin_bills, admin_credentials } = useFlags()
  const { isOrgCredentialsAdminUser } = useAuthentication()
  const {
    adminBillsPath,
    adminMetersPath,
    adminSitesPath,
    adminUsersPath,
    adminCredentialsPath,
  } = useUrl()

  const showBills = Boolean(admin_bills)
  const showCredentials = Boolean(admin_credentials)
  const opsVisible = !isOrgCredentialsAdminUser
  const configVisible = isOrgCredentialsAdminUser ? showCredentials : true
  const showDivider = opsVisible && configVisible

  return (
    <List
      component="nav"
      aria-label={t("moduleTitle.administration")}
      sx={{ mb: { xs: 2, md: 0 }, mr: { xs: 0, md: 4 }, py: 0 }}
    >
      {!isOrgCredentialsAdminUser && (
        <StyledListItemButton
          component={RouterLink}
          selected={pathname.startsWith(adminSitesPath)}
          to={adminSitesPath}
        >
          <ListItemText primary={t("admin.sites")} />
        </StyledListItemButton>
      )}
      {!isOrgCredentialsAdminUser && (
        <StyledListItemButton
          component={RouterLink}
          selected={pathname.startsWith(adminMetersPath)}
          to={adminMetersPath}
        >
          <ListItemText primary={t("admin.meters")} />
        </StyledListItemButton>
      )}
      {!isOrgCredentialsAdminUser && showBills && (
        <StyledListItemButton
          component={RouterLink}
          selected={pathname.startsWith(adminBillsPath)}
          to={adminBillsPath}
        >
          <ListItemText primary={t("admin.bills")} />
        </StyledListItemButton>
      )}
      {showDivider && <Divider aria-hidden sx={{ my: 1 }} />}
      {showCredentials && (
        <StyledListItemButton
          component={RouterLink}
          selected={pathname.startsWith(adminCredentialsPath)}
          to={adminCredentialsPath}
        >
          <ListItemText primary={t("admin.credentials")} />
        </StyledListItemButton>
      )}
      {!isOrgCredentialsAdminUser && (
        <StyledListItemButton
          component={RouterLink}
          selected={pathname.startsWith(adminUsersPath)}
          to={adminUsersPath}
        >
          <ListItemText primary={t("admin.users")} />
        </StyledListItemButton>
      )}
    </List>
  )
}
